'use client';

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LinearProgress, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter } from 'next/navigation';
import { FiArrowRight, FiHelpCircle } from 'react-icons/fi';
import { TbCoins } from 'react-icons/tb';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { fetchBillingSubscription, fetchBillingUsage } from '@/api/billing.api';
import { UserRole } from '@/constants/enums';
import { SIDEBAR_CSS_TRANSITION, SIDEBAR_MOTION_TRANSITION } from './AppLayout.motion';
const USAGE_QUERY_KEY = ['billing', 'usage'];
const BILLING_QUERY_KEY = ['settings', 'billing'];
/** Show banner once ≥90% of the period credit pool is used (or balance is empty). */
const CREDIT_NEAR_LIMIT_RATIO = 0.9;
/** Collapsed footprint — matches the size-9 icon button. */
const COLLAPSED_BANNER_HEIGHT = 36;
/**
 * Sidebar plan/credits banner (reference layout).
 * Shown when credits are exhausted or ≥90% of the plan+pack pool is used.
 * Plan limits stay on relevant pages. Hidden for SuperAdmin / panel routes.
 * Billing CTA only for Admin.
 */
export function AppLayoutUsageBanner({
  expanded,
  roles,
  className
}) {
  const params = useParams();
  const companySlug = params?.companySlug;
  const router = useRouter();
  const isSuperAdmin = roles.includes(UserRole.SuperAdmin);
  const canManageBilling = roles.includes(UserRole.Admin);
  const enabled = Boolean(companySlug) && companySlug !== 'undefined' && !isSuperAdmin;
  const {
    data: usagePayload
  } = useQuery({
    queryKey: [...USAGE_QUERY_KEY, companySlug],
    queryFn: () => fetchBillingUsage(companySlug),
    enabled,
    staleTime: 30_000
  });
  const {
    data: subscription
  } = useQuery({
    queryKey: [...BILLING_QUERY_KEY, companySlug],
    queryFn: () => fetchBillingSubscription(companySlug),
    enabled,
    staleTime: 60_000
  });
  const creditBalance = usagePayload?.creditBalance;
  const credits = useMemo(() => {
    if (!creditBalance || creditBalance.unlimited) return null;
    const available = Math.max(0, creditBalance.availableCredits ?? 0);
    const packCredits = Math.max(0, creditBalance.packCredits ?? 0);
    const planIncluded = Math.max(0, subscription?.plan?.includedCredits ?? 0);
    // Period pool ≈ plan allotment + prepaid packs (carryover above allotment is treated as unused headroom).
    const limit = Math.max(planIncluded + packCredits, available, 1);
    const usedRatio = Math.min(1, Math.max(0, (limit - available) / limit));
    const usedPct = Math.round(usedRatio * 100);
    return {
      available,
      limit,
      usedPct,
      usedRatio,
      unlimited: false
    };
  }, [usagePayload, subscription?.plan?.includedCredits]);
  const showBanner = creditBalance?.unlimited !== true && usagePayload?.contractBilling !== true && credits != null && (credits.available <= 0 || credits.usedRatio >= CREDIT_NEAR_LIMIT_RATIO);

  // Explicit px target so the height animation spans the full transition — 'auto' and
  // grid-tracks end up snapping or compressing the visible motion into part of the ease.
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const update = () => setCardHeight(el.scrollHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, showBanner, credits]);
  if (!enabled || !showBanner || !credits) {
    return null;
  }
  const planName = subscription?.plan?.name?.trim() || 'Current plan';
  const planLabel = /plan$/i.test(planName) ? planName : `${planName} plan`;
  const {
    available,
    usedPct
  } = credits;
  const detailLine = available <= 0 ? '0 credits remaining' : `${available} credit${available === 1 ? '' : 's'} remaining`;
  const helpTitle = available <= 0 ? 'Credits power AI features. Buy a pack or upgrade when you run out.' : 'You are running low on credits. Credits power AI features.';
  const goBilling = () => {
    if (!companySlug || !canManageBilling) return;
    router.push(`/${companySlug}/settings?tab=billing`);
  };
  return <motion.div initial={false} animate={{
    height: expanded ? cardHeight : COLLAPSED_BANNER_HEIGHT
  }} transition={SIDEBAR_MOTION_TRANSITION} className={clsx('relative w-full overflow-hidden', className)} data-testid="sidebar/usage-banner">
            {/* Expanded card — measured for the explicit height target */}
            <div ref={cardRef} aria-hidden={!expanded} className={clsx('flex w-full flex-col gap-2.5 rounded-xl border border-border bg-page/75 backdrop-blur-2xl! p-3', 'transition-[opacity,visibility]', SIDEBAR_CSS_TRANSITION, expanded ? 'visible opacity-100' : 'invisible opacity-0')}>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <TbCoins className="shrink-0 text-sm text-foreground-muted" aria-hidden />
                        <Typography variant="body2" fontWeight={500} noWrap className="text-foreground!">
                            {planLabel}
                        </Typography>
                    </div>
                    <Typography variant="body2" fontWeight={500} className="shrink-0 text-foreground!">
                        {usedPct}%
                    </Typography>
                </div>

                <LinearProgress variant="determinate" value={usedPct} sx={{
        height: 4,
        borderRadius: 999,
        bgcolor: 'action.hover',
        '& .MuiLinearProgress-bar': {
          borderRadius: 999,
          bgcolor: 'text.primary'
        }
      }} />

                <div className="flex items-center gap-1">
                    <Typography variant="caption" color="text.secondary" className="leading-none!">
                        {detailLine}
                    </Typography>
                    <Tooltip title={helpTitle} placement="top" arrow>
                        <span className="inline-flex cursor-help text-foreground-muted" aria-label="About credits">
                            <FiHelpCircle className="text-xs" />
                        </span>
                    </Tooltip>
                </div>

                {canManageBilling ? <button type="button" tabIndex={expanded ? 0 : -1} onClick={goBilling} className="mt-0.5 flex w-full cursor-pointer items-center justify-between rounded-lg bg-foreground px-3 py-2 text-left text-xs! font-medium! text-background transition-opacity hover:opacity-90">
                        <span>View Billing</span>
                        <FiArrowRight className="text-sm! shrink-0" aria-hidden />
                    </button> : null}
            </div>

            {/* Collapsed icon — overlaid, cross-fades in */}
            <div aria-hidden={expanded} className={clsx('absolute inset-0 flex items-center justify-center', 'transition-[opacity,visibility]', SIDEBAR_CSS_TRANSITION, expanded ? 'invisible opacity-0' : 'visible opacity-100')}>
                <Tooltip title={`${planLabel} · ${detailLine}`} placement="right" arrow>
                    <button type="button" tabIndex={expanded ? -1 : 0} onClick={canManageBilling ? goBilling : undefined} className={clsx('flex size-9 cursor-pointer items-center justify-center rounded-lg border border-border bg-page', canManageBilling && 'hover:bg-primary/10')} aria-label={`${planLabel}: ${detailLine}`}>
                        <TbCoins className="text-base text-foreground-muted" />
                    </button>
                </Tooltip>
            </div>
        </motion.div>;
}